import useModal from "../../../useModal";
import Modalheader from "../../../components/header";
import ModalBody from "../../../components/body";
import ModalFooter from "../../../components/footer";
import Button from "components/button";
import UseContext from "hooks/useContext";
import React, { useEffect, useState, useRef, useCallback } from "react";
import ApiDatabase from "server";
import StyledInput from "components/styledInput/StyledInput";
import { CheckIcon, Import } from "assets/icons";
import DatePicker, { registerLocale } from "react-datepicker";
import { fr } from "date-fns/locale";
import axios from "axios";
import { rejectFileIfTooLarge } from "helper/fileSizeLimit";

registerLocale("fr-FR", fr);

const REASON_INAPTITUDE = "Rupture pour inaptitude physique constatée par le médecin";
const REASON_TRIAL_PERIOD = "Fin de période d'essai à l'initiative de l'intérimaire";

function formatDateToIso(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}T00:00:00.000Z`;
}

function formatDateMedium(dateStr) {
  const d = new Date(dateStr);
  const options = { weekday: "short", day: "numeric", month: "short", year: "numeric" };
  return d.toLocaleDateString("fr-FR", options).replace(/^\w/, (c) => c.toUpperCase());
}

const ModalMissionTempWorkerInterrupt = () => {
  const { toggle } = useModal();
  const { missionActionData, setMissionStatusUpdate } = UseContext();
  const [step, setStep] = useState("select");
  const [isWaiting, setIsWaiting] = useState(true);
  const [missionData, setMissionData] = useState(null);
  const [companyName, setCompanyName] = useState("");
  const [reasons, setReasons] = useState([]);
  const [reasonSelected, setReasonSelected] = useState("");
  const [description, setDescription] = useState("");
  const [endDate, setEndDate] = useState(new Date());
  const [trialPeriod, setTrialPeriod] = useState(null);
  const [countdown, setCountdown] = useState(3);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [dateError, setDateError] = useState("");

  // File upload state
  const [singleFile, setSingleFile] = useState(null);
  const [titleFile, setTitleFile] = useState("");
  const [uploadedFilePath, setUploadedFilePath] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const timerRef = useRef(null);
  const intervalRef = useRef(null);
  useEffect(() => {
    if (missionActionData?._id) {
      setIsWaiting(true);
      ApiDatabase.getTempWorkerMissionStatusInfo(
        { idMission: missionActionData._id },
        (data) => {
          setMissionData(data.mission);
          setCompanyName(data.company?.name_commercial || "");
          setReasons(data.reasons || []);
          setTrialPeriod(data.mission?.trial_period || null);
          setIsWaiting(false);
        },
        (err) => {
          console.error(err);
          setIsWaiting(false);
        }
      );
    }
  }, [missionActionData]);

  useEffect(() => {
    if (reasonSelected === REASON_TRIAL_PERIOD && trialPeriod?.end && endDate) {
      const te = new Date(trialPeriod.end);
      const endDay = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
      const trialDay = new Date(te.getFullYear(), te.getMonth(), te.getDate());
      if (endDay > trialDay) {
        setDateError("La date indiquée dépasse la période d'essai");
      } else {
        setDateError("");
      }
    } else {
      setDateError("");
    }
  }, [endDate, reasonSelected, trialPeriod]);

  const clearTimers = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (intervalRef.current) clearInterval(intervalRef.current);
  }, []);

  useEffect(() => {
    return () => clearTimers();
  }, [clearTimers]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const missionStart = missionData?.duration?.datestart
    ? new Date(new Date(missionData.duration.datestart).toISOString().split("T")[0] + "T00:00:00")
    : undefined;
  const minDate = missionStart && missionStart > today ? missionStart : today;
  const maxDate = missionData?.duration?.dateend
    ? new Date(new Date(missionData.duration.dateend).toISOString().split("T")[0] + "T00:00:00")
    : undefined;

  const startCountdown = () => {
    setStep("countdown");
    setCountdown(3);
    setIsSubmitted(false);

    intervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    timerRef.current = setTimeout(() => {
      submitAction();
    }, 3000);
  };

  const submitAction = () => {
    setIsSubmitted(true);
    ApiDatabase.postTempWorkerMissionStatusChange(
      {
        idMission: missionActionData._id,
        newStatus: "interrupted",
        reason: reasonSelected,
        description,
        endDate: formatDateToIso(endDate),
        filePath: uploadedFilePath,
        fileName: uploadedFileName,
      },
      () => {
        setMissionStatusUpdate({ status: "interrupted" });
      },
      (err) => console.error("Error interrupting mission:", err)
    );
  };

  const handleCancelCountdown = () => {
    clearTimers();
    if (reasonSelected === REASON_INAPTITUDE) {
      setStep("datepicker");
    } else if (reasonSelected === REASON_TRIAL_PERIOD) {
      setStep("datepicker");
    } else {
      setStep("datepicker");
    }
    setCountdown(3);
  };

  const handleFileChange = (e) => {
    if (rejectFileIfTooLarge(e)) return;
    if (e.target.files[0]) {
      setSingleFile(e.target.files[0]);
      setTitleFile(e.target.files[0].name);
    }
  };

  const handleUploadFile = async () => {
    if (!singleFile) return;
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("otherDocument", singleFile);
      formData.append("otherDocumentName", "Visites médicales");
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}upload/files`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      const first = res.data?.details?.[0];
      const upload = first?.info?.payload?.upload;
    } catch (err) {
      console.error("File upload error:", err);
    } finally {
      setIsUploading(false);
    }
  };

  const selectList = [
    { value: "", label: "Sélectionner" },
    ...reasons.map((r) => ({ value: r.name, label: r.name })),
  ];

  const handleSelectStep = () => {
    if (!reasonSelected) return;
    if (reasonSelected === REASON_INAPTITUDE) {
      setStep("upload");
    } else {
      setStep("datepicker");
    }
  };

  // Countdown / confirmation step
  if (step === "countdown") {
    return (
      <>
        <ModalBody>
          <div className="flex flex-col gap-4 items-center text-center py-4">
            <div className="flex items-center justify-center w-24 h-24 rounded-full bg-green-100">
              <CheckIcon w={32} h={32} color="#111827" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">
              Mission interrompue
            </h2>
            <p className="text-gray-600">
              L'agence et l'entreprise ont été informées de l'interruption de la
              mission
            </p>
          </div>
        </ModalBody>
        <ModalFooter>
          {isSubmitted ? (
            <Button size="LG" color="SECONDARY" onClick={toggle}>
              Retour
            </Button>
          ) : (
            <Button
              size="LG"
              color="SECONDARY"
              onClick={handleCancelCountdown}
            >
              Annuler ({countdown} s)
            </Button>
          )}
        </ModalFooter>
      </>
    );
  }

  // File upload step (inaptitude only)
  if (step === "upload") {
    return (
      <>
        <Modalheader hide={toggle} hideShow={true}>
          Ajoutez votre avis d'inaptitude
        </Modalheader>
        <ModalBody>
          <div className="flex flex-col gap-4">
            <p className="text-gray-700">
              Importez l'avis d'inaptitude délivré par le médecin du travail
              pour justifier la rupture de votre mission
            </p>
            <form className="flex flex-col gap-1">
              <label className="w-full flex flex-row border border-gray-300 rounded-lg h-11 shadow-sm cursor-pointer">
                <div className="ft-sm text-gray-700 w-1/4 p-4 flex items-center justify-center gap-2 border-r border-gray-300">
                  <Import color={"#374151"} wh={14} />
                  Importer
                </div>
                <div className="ft-sm truncate overflow-hidden text-gray-400 flex items-center ml-3">
                  {titleFile || "Aucun fichier"}
                </div>
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,application/pdf"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </label>
              <span className="ft-xs text-gray-400">
                Formats acceptés : PDF, JPG, PNG (10Mo max)
              </span>
            </form>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button size="LG" color="SECONDARY" onClick={() => setStep("select")}>
            Retour
          </Button>
          <Button
            size="LG"
            color={!singleFile || isUploading ? "DISABLED" : "PRIMARY"}
            disabled={!singleFile || isUploading}
            onClick={singleFile && !isUploading ? handleUploadFile : undefined}
          >
            {isUploading ? "Envoi..." : "Confirmer"}
          </Button>
        </ModalFooter>
      </>
    );
  }

  // DatePicker step
  if (step === "datepicker") {
    const isTrialPeriod = reasonSelected === REASON_TRIAL_PERIOD;
    const canConfirm = endDate && !dateError;

    return (
      <>
        <Modalheader hide={toggle} hideShow={true}>
          Date de fin de mission prévue
        </Modalheader>
        <ModalBody>
          <div className="flex flex-col gap-4">
            <p className="text-gray-700">
              Indiquez la date de fin de mission prévue.
            </p>
            <div className="flex flex-col gap-1">
              <label className="text-gray-500">Date de fin de mission</label>
              <DatePicker
                dateFormat="dd/MM/yyyy"
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                minDate={minDate}
                maxDate={maxDate}
                locale="fr-FR"
                popperPlacement="auto"
                popperProps={{ strategy: 'fixed' }}
                popperClassName="z-[9999]"
                className="w-full h-10 pl-3 pr-8 border rounded-lg text-center"
              />
              {isTrialPeriod && trialPeriod?.end && (
                <p className="text-sm text-gray-500 mt-1">
                  Fin de période d'essai : {formatDateMedium(trialPeriod.end)}
                </p>
              )}
              {dateError && (
                <p className="text-sm text-red-500 mt-1">{dateError}</p>
              )}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            size="LG"
            color="SECONDARY"
            onClick={() => {
              if (reasonSelected === REASON_INAPTITUDE) setStep("upload");
              else setStep("select");
            }}
          >
            Retour
          </Button>
          <Button
            size="LG"
            color={canConfirm ? "PRIMARY" : "DISABLED"}
            disabled={!canConfirm}
            onClick={canConfirm ? startCountdown : undefined}
          >
            Confirmer
          </Button>
        </ModalFooter>
      </>
    );
  }

  // Default: reason select step
  return (
    <>
      <Modalheader hide={toggle} hideShow={true}>
        Quel problème rencontrez vous
      </Modalheader>
      <ModalBody>
        {isWaiting ? (
          <div className="animate-pulse flex flex-col gap-3">
            <div className="h-4 bg-gray-200 rounded w-3/4" />
            <div className="h-10 bg-gray-200 rounded" />
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <p className="text-gray-700">
              Sélectionne le motif lié à ton problème rencontré sur la mission "
              {missionData?.title} n°{missionData?.id_mission}" à {companyName}
            </p>
            <StyledInput
              type="select"
              label="Motif"
              placeholder="Sélectionner"
              value={reasonSelected}
              onChange={(e) => setReasonSelected(e.target.value)}
              list={selectList}
            />
            {reasonSelected === REASON_TRIAL_PERIOD && (
              <StyledInput
                type="textarea"
                label="Dites nous-en plus"
                placeholder="ex: la mission ne me convient plus"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            )}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button size="LG" color="SECONDARY" onClick={toggle}>
          Annuler
        </Button>
        <Button
          size="LG"
          color={!reasonSelected ? "DISABLED" : "PRIMARY"}
          disabled={!reasonSelected}
          onClick={reasonSelected ? handleSelectStep : undefined}
        >
          Confirmer
        </Button>
      </ModalFooter>
    </>
  );
};

export default ModalMissionTempWorkerInterrupt;
